import React, { useState, useEffect, useCallback } from "react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { useHistory } from "react-router-dom"
import ReactPaginate from "react-paginate"
import debounce from "lodash.debounce"
import { URLS } from "../../Url"
import moment from "moment"
import axios from "axios"
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap"

function AllOrders() {
  const history = useHistory()
  const authUser = JSON.parse(localStorage.getItem("authUser")) || {}
  const token = authUser.token

  const [orders, setOrders] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState(
    sessionStorage.getItem("str") || "All"
  )
  const [loading, setLoading] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({
    _id: "",
    status: "",
    deliveryDate: "",
    reason: "",
    date: "",
  })

  const statusOptions = [
    { value: "All", label: "All Orders" },
    { value: "new", label: "New" },
    { value: "pending", label: "Pending" },
    { value: "cancelled", label: "Cancelled" },
    { value: "shipped", label: "Shipped" },
    { value: "rejected", label: "Rejected" },
    { value: "paid", label: "Paid" },
    { value: "due", label: "Due" },
    { value: "closed", label: "Closed" },
  ]

  const fetchOrders = useCallback(
    async (page = 1, status = statusFilter) => {
      if (!token) {
        toast.error("Authentication token is missing. Please log in.")
        return
      }

      setLoading(true)
      try {
        let statusArray = [status]

        const response = await axios.post(
          URLS.GetAllOrders,
          { status: statusArray, page },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        setOrders(response.data.data || [])
        setTotalPages(response.data.totalPages || 0)
        setCurrentPage(response.data.currentPage || 1)
        setTotalCount(response.data.totalCount || 0)
      } catch (error) {
        console.error("Error fetching orders:", error)
        toast.error(error.response?.data?.message || "Failed to fetch orders")
      } finally {
        setLoading(false)
      }
    },
    [statusFilter, token]
  )

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const handleStatusFilter = e => {
    const value = e.target.value
    setStatusFilter(value)
    setCurrentPage(1)
    fetchOrders(1, value)
  }

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1
    setCurrentPage(newPage)
    fetchOrders(newPage)
  }

  const debouncedSearch = useCallback(
    debounce(value => {
      setSearchTerm(value.toLowerCase())
    }, 300),
    []
  )

  const handleSearch = e => {
    debouncedSearch(e.target.value)
  }

  const filteredOrders = orders.filter(
    order =>
      order.orderUnquieId?.toLowerCase().includes(searchTerm) ||
      order.customerName?.toLowerCase().includes(searchTerm) ||
      order.customerEmail?.toLowerCase().includes(searchTerm) ||
      order.customerMobile?.toLowerCase().includes(searchTerm)
  )

  const viewOrder = order => {
    sessionStorage.setItem("OrderId", order._id)
    history.push("/ViewOrder")
  }

  const getStatusBadge = status => {
    const statusLower = status?.toLowerCase()
    const badgeStyles = {
      new: { color: "primary", label: "New" },
      pending: { color: "warning", label: "Pending" },
      shipped: { color: "info", label: "Shipped" },
      due: { color: "danger", label: "Due" },
      paid: { color: "success", label: "Paid" },
      completed: { color: "success", label: "Closed" },
      cancelled: { color: "danger", label: "Cancelled" },
      rejected: { color: "dark", label: "Rejected" },
    }
    const { color, label } = badgeStyles[statusLower] || {
      color: "secondary",
      label: status || "Unknown",
    }
    return (
      <h6 color={color} className={`py-1 px-2 1 text-${color}`}>
        {label}
      </h6>
    )
  }

  const getPaymentBadge = status => {
    const statusLower = status?.toLowerCase()
    const badgeStyles = {
      success: { color: "success", label: "Success" },
      pending: { color: "warning", label: "Pending" },
      failed: { color: "danger", label: "Failed" },
    }
    const { color, label } = badgeStyles[statusLower] || {
      color: "secondary",
      label: status || "Unknown",
    }
    return <h6 className={`py-1 px-2 1 text-${color}`}>{label}</h6>
  }

  const resetFilters = () => {
    setSearchTerm("")
    setStatusFilter("All")
    setCurrentPage(1)
    fetchOrders(1, "All")
  }

  const handleChange = e => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const updateOrderStatus = async () => {
    if (!form._id || !form.status) {
      toast.error("Please select a status")
      return
    }

    setModalLoading(true)
    try {
      let response
      switch (form.status) {
        case "pending":
          response = await axios.post(
            `${URLS.OrderAccept}`,
            {
              orderId: form._id,
              deliveryDate: form.deliveryDate,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          break
        case "rejected":
          if (!form.reason.trim()) {
            toast.error("Please provide a reason for rejection")
            return
          }
          response = await axios.post(
            `${URLS.OrderReject}`,
            {
              orderId: form._id,
              reason: form.reason,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          break
        case "shipped":
          response = await axios.post(
            `${URLS.OrderShipped}`,
            {
              orderId: form._id,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          break
        case "paid":
          response = await axios.post(
            `${URLS.OrderPaymentConfirmation}`,
            {
              bookingId: form._id,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          break
        case "closed":
          response = await axios.post(
            `${URLS.OrderCompleted}`,
            {
              orderId: form._id,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          break
        default:
          toast.error("Invalid status selected")
          return
      }

      toast.success(response.data.message || "Order updated successfully")
      fetchOrders(currentPage)
      setModalOpen(false)
      setForm({ _id: "", status: "", deliveryDate: "", reason: "", date: "" })
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update order")
    } finally {
      setModalLoading(false)
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    updateOrderStatus()
  }

  const openEditPopup = (order, action) => {
    setForm({
      _id: order._id,
      status: action,
      deliveryDate: "",
      reason: "",
      date: "",
    })
    setModalOpen(true)
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Bizzmate Supplier" breadcrumbItem="All Orders" />
        <Row>
          <Col>
            <Card>
              <CardBody>
                <Row className="mb-3 align-items-center">
                  <Col md={4}>
                    <h4 className="card-title mb-0">
                      All Orders ({totalCount})
                    </h4>
                  </Col>
                  <Col md={8} className="d-flex justify-content-end gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={resetFilters}
                      className="d-flex align-items-center btn-sm"
                      aria-label="Reset filters"
                    >
                      <i className="fas fa-sync-alt me-2" /> Reset
                    </Button>
                    {![
                      "All",
                      "new",
                      "pending",
                      "cancelled",
                      "shipped",
                      "rejected",
                      "closed",
                      "confirmed",
                      "delivered",
                      "due",
                      "past due",
                      "paid",
                    ].includes(sessionStorage.getItem("str")) ? (
                      <>
                        <Input
                          type="select"
                          name="statusFilter"
                          value={statusFilter}
                          onChange={handleStatusFilter}
                          className="w-auto"
                          aria-label="Filter by order status"
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </>
                    ) : (
                      <></>
                    )}
                    <div className="search-box" style={{ width: "200px" }}>
                      <Input
                        type="search"
                        placeholder="Search orders..."
                        onChange={handleSearch}
                        aria-label="Search orders by ID, name, email, or mobile"
                      />
                    </div>
                  </Col>
                </Row>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading orders...</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th scope="col" style={{ width: "50px" }}>
                            #
                          </th>
                          <th scope="col">Order ID</th>
                          <th scope="col">Date</th>
                          <th scope="col">Customer</th>
                          <th scope="col">Amount</th>
                          <th scope="col">Status</th>
                          <th scope="col">Payment</th>
                          <th scope="col" style={{ width: "220px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.length > 0 ? (
                          filteredOrders.map((order, index) => (
                            <tr key={order._id}>
                              <td>{(currentPage - 1) * 10 + index + 1}</td>
                              <td>
                                <span className="fw-semibold">
                                  {order.orderUnquieId}
                                </span>
                              </td>
                              <td>
                                {order.logCreatedDate &&
                                moment(order.logCreatedDate).isValid() ? (
                                  <>
                                    {moment(order.logCreatedDate).format(
                                      "DD MMM YYYY"
                                    )}
                                    <br />
                                    <small className="text-muted">
                                      {moment(order.logCreatedDate).format(
                                        "h:mm A"
                                      )}
                                    </small>
                                  </>
                                ) : (
                                  <span className="text-muted">
                                    Invalid Date
                                  </span>
                                )}
                              </td>
                              <td>
                                <h6 className="mb-1">{order.customerName}</h6>
                                <small className="text-muted">
                                  {order.customerEmail}
                                </small>
                                <br />
                                <small>{order.customerMobile}</small>
                              </td>
                              <td>
                                ₹{order.totalAmount?.toFixed(2) || "0.00"}
                                {order.couponDiscount > 0 && (
                                  <div className="text-success small">
                                    <del className="text-muted me-1">
                                      ₹
                                      {(
                                        parseFloat(order.totalAmount || 0) +
                                        parseFloat(order.couponDiscount || 0)
                                      ).toFixed(2)}
                                    </del>
                                    Saved ₹{order.couponDiscount}
                                  </div>
                                )}
                              </td>
                              <td>{getStatusBadge(order.status)}</td>
                              <td>{getPaymentBadge(order.paymentStatus)}</td>
                              <td>
                                {order.status === "new" && (
                                  <>
                                    {/* <Button
                                      onClick={() =>
                                        openEditPopup(order, "pending")
                                      }
                                      color="primary" 
                                      size="sm"
                                      className="me-2 mb-1"
                                      aria-label={`Accept order ${order.orderUnquieId}`}
                                      outline
                                    > */}
                                    <i
                                      className="bx bx-check-circle me-2 text-primary"
                                      style={{
                                        fontSize: "22px",
                                        cursor: "pointer",
                                      }}
                                      title="Accept"
                                      onClick={() =>
                                        openEditPopup(order, "pending")
                                      }
                                    />
                                    {/* Accept */}
                                    {/* </Button> */}
                                    {/* <Button
                                      onClick={() =>
                                        openEditPopup(order, "rejected")
                                      }
                                      color="primary"
                                      size="sm"
                                      className="me-2 mb-1"
                                      aria-label={`Reject order ${order.orderUnquieId}`}
                                      outline */}
                                    {/* > */}
                                    <i
                                      className="bx bx-x-circle me-2 text-primary"
                                      style={{
                                        fontSize: "22px",
                                        cursor: "pointer",
                                      }}
                                      title="Reject"
                                      onClick={() =>
                                        openEditPopup(order, "rejected")
                                      }
                                    />
                                    {/* Reject
                                    </Button> */}
                                  </>
                                )}
                                {order.status === "pending" && (
                                  <>
                                    {/* <Button
                                    onClick={() =>
                                      openEditPopup(order, "shipped")
                                    }
                                    color="primary"
                                    size="sm"
                                    className="me-2 mb-1"
                                    aria-label={`Mark order ${order.orderUnquieId} as shipped`}
                                    outline
                                  > */}
                                    <i
                                      className="bx bx-analyse me-2 text-primary"
                                      title="Mark as Shipped"
                                      style={{
                                        fontSize: "22px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        openEditPopup(order, "shipped")
                                      }
                                    />
                                    {/* Mark as Shipped */}
                                    {/* </Button> */}
                                  </>
                                )}
                                {order.status === "shipped" && (
                                  <>
                                    {/* <Button
                                    onClick={() =>
                                      openEditPopup(order, "closed")
                                    }
                                    color="primary"
                                    size="sm"
                                    className="me-2 mb-1"
                                    aria-label={`Mark order ${order.orderUnquieId} as Closed`}
                                    outline
                                  > */}
                                    <i
                                      className="bx bx-transfer-alt me-2 text-primary"
                                      title="Mark Order Delivered"
                                      style={{
                                        fontSize: "22px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        openEditPopup(order, "closed")
                                      }
                                    />
                                    {/* Mark Order Delivered
                                  </Button> */}
                                  </>
                                )}
                                {order.status === "paid" && (
                                  <>
                                    {/* <Button
                                    onClick={() => openEditPopup(order, "paid")}
                                    color="primary"
                                    size="sm"
                                    className="me-2 mb-1"
                                    aria-label={`Confirm payment for order ${order.orderUnquieId}`}
                                    outline
                                  > */}
                                    <i
                                      className="bx bx-check-circle me-2 text-primary"
                                      title="Confirm Payment"
                                      style={{
                                        fontSize: "22px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        openEditPopup(order, "paid")
                                      }
                                    />
                                    {/* Confirm Payment */}
                                    {/* </Button> */}
                                  </>
                                )}
                                {/* <Button
                                  onClick={() => viewOrder(order)}
                                  color="primary"
                                  size="sm"
                                  className="mb-1"
                                  aria-label={`View order ${order.orderUnquieId}`}
                                  outline
                                > */}
                                <i
                                  className="mdi mdi-eye-circle-outline text-primary"
                                  title="View Order"
                                  onClick={() => viewOrder(order)}
                                  style={{
                                    fontSize: "22px",
                                    cursor: "pointer",
                                  }}
                                />
                                {/* View */}
                                {/* </Button> */}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              {searchTerm || statusFilter !== "All"
                                ? "No orders match your filter criteria"
                                : "No orders found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-end mt-3">
                    <ReactPaginate
                      previousLabel="Previous"
                      nextLabel="Next"
                      pageCount={totalPages}
                      onPageChange={handlePageChange}
                      forcePage={currentPage - 1}
                      containerClassName="pagination"
                      previousLinkClassName="page-link"
                      nextLinkClassName="page-link"
                      disabledClassName="disabled"
                      activeClassName="active"
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      aria-label="Pagination"
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Modal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          centered
          size="sm"
        >
          <ModalHeader toggle={() => setModalOpen(false)}>
            Update Order Status
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              {form.status === "pending" && (
                <FormGroup className="mb-3">
                  <Label for="deliveryDate">
                    Delivery Date <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="deliveryDate"
                    type="date"
                    name="deliveryDate"
                    value={form.deliveryDate}
                    onChange={handleChange}
                    required
                    aria-required="true"
                  />
                </FormGroup>
              )}
              {form.status === "rejected" && (
                <FormGroup className="mb-3">
                  <Label for="reason">
                    Reason for Rejection <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="reason"
                    type="textarea"
                    name="reason"
                    value={form.reason}
                    onChange={handleChange}
                    placeholder="Enter reason for rejection"
                    rows="4"
                    required
                    aria-required="true"
                  />
                </FormGroup>
              )}
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                type="button"
                onClick={() => {
                  setModalOpen(false)
                  setForm({
                    _id: "",
                    status: "",
                    deliveryDate: "",
                    reason: "",
                    date: "",
                  })
                }}
                disabled={modalLoading}
                aria-label="Cancel"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={modalLoading}
                aria-label="Update order status"
              >
                {modalLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  "Update Status"
                )}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  )
}

export default AllOrders
